// src/components/dashboard/ActiveMotionsWidget.js
// Updated: 10/31/2025 06:22 PM EDT
// --- THIS IS A NEW FILE ---

import React, { useState, useEffect } from 'react';
import { db } from '../../firebaseConfig';
import { useAuth } from '../../context/AuthContext';
import {
  collection,
  query,
  where,
  onSnapshot,
  orderBy,
  collectionGroup,
  getDocs,
  limit,
} from 'firebase/firestore';
import { Link } from 'react-router-dom';
import { Vote, ChevronRight, AlertCircle } from 'lucide-react';

// Small spinner for the widget
const WidgetSpinner = () => (
  <div className="flex h-32 items-center justify-center">
    <div className="h-6 w-6 animate-spin rounded-full border-2 border-dashed border-blue-500"></div>
  </div>
);

export default function ActiveMotionsWidget() {
  const [motionsToVoteOn, setMotionsToVoteOn] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const { userData, currentUser } = useAuth();
  const currentUserId = currentUser?.uid;
  const isAdmin = userData?.role === 'admin';

  useEffect(() => {
    if (!currentUserId || !userData?.orgId) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    // Step 1: Get IDs of motions the user has already voted on
    const fetchVotedMotionIds = async () => {
      const votesQuery = query(
        collectionGroup(db, 'votes'),
        where('userId', '==', currentUserId),
        where('orgId', '==', userData.orgId)
      );
      const snapshot = await getDocs(votesQuery);
      const ids = new Set();
      snapshot.forEach((doc) => {
        ids.add(doc.ref.parent.parent.id); // Add motionId to the set
      });
      return ids;
    };

    // Step 2: Fetch open motions and filter
    const fetchOpenMotions = (votedIds) => {
      const q = query(
        collection(db, 'motions'),
        where('orgId', '==', userData.orgId),
        where('status', '==', 'open'),
        orderBy('createdAt', 'desc'),
        limit(5) // Only show the 5 most recent open motions
      );

      const unsubscribe = onSnapshot(
        q,
        (querySnapshot) => {
          const motionsData = [];
          querySnapshot.forEach((doc) => {
            motionsData.push({ id: doc.id, ...doc.data() });
          });

          // Filter out motions the user can't vote on or has already voted on
          const filteredMotions = motionsData.filter((motion) => {
            // Check if already voted
            if (votedIds.has(motion.id)) {
              return false;
            }
            // Admins can see all
            if (isAdmin) {
              return true;
            }
            // Check if user is in the allowedVoters list (if it exists)
            const isRestricted = motion.allowedVoters && Array.isArray(motion.allowedVoters);
            if (isRestricted) {
              return motion.allowedVoters.includes(currentUserId);
            }
            // If not restricted, all members can vote
            return true;
          });

          setMotionsToVoteOn(filteredMotions);
          setIsLoading(false);
        },
        (err) => {
          console.error('Error fetching motions for widget:', err);
          setError('Could not load motions.');
          setIsLoading(false);
        }
      );
      return unsubscribe;
    };

    // Run the process
    let unsubscribeMotions;
    fetchVotedMotionIds()
      .then((votedIds) => {
        unsubscribeMotions = fetchOpenMotions(votedIds);
      })
      .catch((err) => {
        console.error('Error fetching voted IDs:', err);
        setError('Could not check vote status.');
        setIsLoading(false);
      });

    return () => {
      if (unsubscribeMotions) {
        unsubscribeMotions();
      }
    };
  }, [currentUserId, userData?.orgId, isAdmin]); // Rerun if user or org changes

  return (
    <div className="rounded-lg bg-white p-6 shadow-lg h-full">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-gray-800">Active Motions</h2>
        <Vote className="h-6 w-6 text-blue-500" />
      </div>
      <p className="mt-1 text-sm text-gray-500">Motions that require your vote.</p>

      <div className="mt-4">
        {isLoading ? (
          <WidgetSpinner />
        ) : error ? (
          <div className="flex h-32 items-center justify-center text-sm text-red-600">
            <AlertCircle className="mr-2 h-4 w-4" /> {error}
          </div>
        ) : motionsToVoteOn.length === 0 ? (
          <div className="flex h-32 items-center justify-center">
            <p className="text-center text-gray-500">
              All caught up!
              <br />
              You have no pending motions to vote on.
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-gray-200">
            {motionsToVoteOn.map((motion) => (
              <li key={motion.id} className="py-3">
                <Link
                  to="/voting"
                  className="group flex items-center justify-between"
                >
                  <div>
                    <p className="font-medium text-gray-900 group-hover:text-blue-600">
                      {motion.title}
                    </p>
                    <p className="text-sm text-gray-500">
                      {motion.voteType === 'named' ? 'Named Ballot' : 'Secret Ballot'}
                    </p>
                  </div>
                  <ChevronRight className="h-5 w-5 text-gray-400 group-hover:text-blue-600" />
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}